import { NavLink, Outlet, useLocation, Navigate } from "react-router-dom";
import { PageHeader } from "@/components/lembra/PageHeader";
import { PageMeta } from "@/components/lembra/PageMeta";
import { PageShell } from "@/components/lembra/PageShell";

/** Legacy URLs still deep-link into anchored sections on the program page. */
export const LEGACY_PROGRAM_PATHS = ["celebrations", "budget", "tiers"] as const;

export const SETTINGS_SECTIONS = [
  { path: "account", label: "Account", scrollId: "account" },
  { path: "company", label: "Company", scrollId: "company" },
  { path: "program", label: "Program & budget", scrollId: "program" },
  { path: "payment", label: "Payment method", scrollId: "payment" },
  { path: "autopilot", label: "Autopilot", scrollId: "autopilot" },
  { path: "notifications", label: "Notifications", scrollId: "notifications" },
] as const;

export type SettingsSectionPath = (typeof SETTINGS_SECTIONS)[number]["path"];

export function settingsScrollIdForPath(pathname: string): string | null {
  const segment = pathname.replace(/^\/settings\/?/, "").split("/")[0];
  if ((LEGACY_PROGRAM_PATHS as readonly string[]).includes(segment)) {
    return "program";
  }
  const match = SETTINGS_SECTIONS.find((s) => s.path === segment);
  return match?.scrollId ?? null;
}

/** Anchor id within the program page when arriving via a legacy sub-route. */
export function settingsProgramScrollAnchor(pathname: string): string | null {
  const segment = pathname.replace(/^\/settings\/?/, "").split("/")[0];
  if ((LEGACY_PROGRAM_PATHS as readonly string[]).includes(segment)) {
    return segment;
  }
  return null;
}

/** Layout shell: left sub-nav + one settings section per route. */
export function SettingsLayout() {
  const location = useLocation();
  const isSettingsIndex =
    location.pathname === "/settings" || location.pathname === "/settings/";

  if (isSettingsIndex) {
    return <Navigate to="/settings/account" replace />;
  }

  const hash = location.hash.replace(/^#/, "");
  if (hash) {
    const section = SETTINGS_SECTIONS.find((s) => s.scrollId === hash);
    if (section) {
      return <Navigate to={`/settings/${section.path}`} replace />;
    }
    if ((LEGACY_PROGRAM_PATHS as readonly string[]).includes(hash)) {
      return <Navigate to={`/settings/program#${hash}`} replace />;
    }
  }

  return (
    <PageShell width="narrow">
      <PageMeta
        title="Settings · Lembra"
        description="Manage your Lembra account settings, cadence, and notifications."
        path="/settings"
        noindex
      />
      <PageHeader
        title="Settings"
        emphasis="Settings"
        subtitle="Account, company, program defaults, payment method, and how Lembra runs in the background."
      />

      <div className="mt-6 flex flex-col gap-6 md:flex-row md:items-start">
        <nav
          className="flex shrink-0 flex-row flex-wrap gap-1 md:sticky md:top-24 md:w-44 md:flex-col md:gap-0.5 md:self-start"
          aria-label="Settings sections"
        >
          {SETTINGS_SECTIONS.map(({ path, label }) => (
            <NavLink
              key={path}
              to={`/settings/${path}`}
              className={({ isActive }) =>
                `rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[rgba(29,52,97,0.08)] text-navy"
                    : "text-[rgba(29,52,97,0.55)] hover:bg-[rgba(29,52,97,0.04)] hover:text-navy"
                }`
              }
            >
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="min-w-0 flex-1">
          <Outlet />
        </div>
      </div>
    </PageShell>
  );
}
